const calculateRebarWeight = (diameter, length) => {
    const density = 7.85
    const radius = diameter / 2 * 1e-1
    const A = Math.PI * radius ** 2
    const V = A * length
    const mass = V * density
    return mass * 1e-3
}

const calculateWasteWeight = (patterns) => {
    let totalWasteWeight = 0;
    patterns.forEach(pattern => {
        totalWasteWeight += calculateRebarWeight(
            pattern.diameter,
            pattern.nLogs * pattern.waste);
    })
    return totalWasteWeight;
}

const calculatePatternLengthWithoutWaste = (pattern) => {
    let length = 0;
    pattern.cuts.forEach(cut => {
        length += cut.cutLength * cut.nCuts
    })
    if ("overproductions" in pattern) {
        pattern.overproductions.forEach(cut => {
            length += cut.cutLength * cut.nCuts;
        })
    }
    return pattern.nLogs * length;
}

const calculateTotalPatternLength = (pattern) => {
    let length = 0;
    pattern.cuts.forEach(cut => {
        length += cut.cutLength * cut.nCuts
    })
    length += pattern.waste;
    if ("overproductions" in pattern) {
        pattern.overproductions.forEach(cut => {
            length += cut.cutLength * cut.nCuts;
        })
    }
    return pattern.nLogs * length;
}

const calculateTotalPatternsWeight = (patterns) => {
    let totalWeight = 0;
    patterns.forEach(pattern => {
        totalWeight += calculateRebarWeight(
            parseFloat(pattern.diameter),
            calculateTotalPatternLength(pattern));
    })
    return totalWeight;
}

const calculateTotalPatternsWeightExceptWaste = (patterns) => {
    let totalWeight = 0;
    patterns.forEach(pattern => {
        totalWeight += calculateRebarWeight(
            parseFloat(pattern.diameter),
            calculatePatternLengthWithoutWaste(pattern));
    })
    return totalWeight;
}

// Total Weight
export const getTotalWeightExceptPreCutWaste = (patterns) => {
    const preCutPatterns = patterns.filter(pattern => pattern.preCut);
    const totalWeight = calculateTotalPatternsWeight(patterns);
    const preCutWasteWeight = calculateWasteWeight(preCutPatterns);
    const result = totalWeight - preCutWasteWeight;

    return Math.round(result, 0).toLocaleString();
}

// Total Waste
export const getTotalWasteWeightExceptPreCut = (patterns) => {
    const preCutPatterns = patterns.filter(pattern => pattern.preCut);
    const wasteWeight = calculateWasteWeight(patterns);
    const preCutWasteWeight = calculateWasteWeight(preCutPatterns);
    const result = wasteWeight - preCutWasteWeight;
    return Math.round(result, 0).toLocaleString();
}

// PreCut Weight
export const getTotalPreCutWeight = (patterns) => {
    const preCutPatterns = patterns.filter(pattern => pattern.preCut);
    const preCutRebarsWeight = calculateTotalPatternsWeightExceptWaste(preCutPatterns);
    return Math.round(preCutRebarsWeight, 0).toLocaleString();
}

// Order Weight
export const getOrderWeight = (patterns, currentFloors) => {
    const patternsInUpperFloors = patterns.filter(pattern => {
        const patternMinFloor = Math.min(...pattern.floors);
        const currentMinFloor = Math.min(...currentFloors);
        return pattern.floors.some(floor => currentFloors.includes(floor))
        && patternMinFloor >= currentMinFloor
    });
    const preCutPatterns = patternsInUpperFloors.filter(pattern => pattern.preCut);

    const totalWeight = calculateTotalPatternsWeight(patternsInUpperFloors);
    const preCutWasteWeight = calculateWasteWeight(preCutPatterns);
    const result = totalWeight - preCutWasteWeight;

    return Math.round(result, 0).toLocaleString();
}

export const getOrderWeightExceptPreCut = (patterns, currentFloors) => {
    const patternsInUpperFloors = patterns.filter(pattern => {
        const patternMinFloor = Math.min(...pattern.floors);
        const currentMinFloor = Math.min(...currentFloors);
        return pattern.floors.some(floor => currentFloors.includes(floor))
        && patternMinFloor >= currentMinFloor
    });
    const preCutPatterns = patternsInUpperFloors.filter(pattern => pattern.preCut);

    const totalWeight = calculateTotalPatternsWeight(patternsInUpperFloors);
    const preCutWeight = calculateTotalPatternsWeight(preCutPatterns);
    const result = totalWeight - preCutWeight;

    return Math.round(result, 0).toLocaleString();
}

// Outage Weight
export const getOutageWeight = (patterns, minOutageLength) => {
    const outagePatterns = patterns.filter(pattern => pattern.waste >= minOutageLength);
    const outageWeight = calculateWasteWeight(outagePatterns);
    const preCutWasteWeight = calculateWasteWeight(outagePatterns.filter(pattern => pattern.preCut));
    const result = outageWeight - preCutWasteWeight;

    return Math.round(result, 0).toLocaleString();
}

export const getSubstitutionWeight = (patterns) => {
    let substitutionWeight = 0;
    patterns.forEach(pattern => {
        if ("substitutions" in pattern) {
            const substitutions = pattern.substitutions;
            let substitutionLength = 0;
            substitutions.forEach(substitution => {
                substitutionLength += substitution.number * substitution.subs_by;
            });
            substitutionWeight += calculateRebarWeight(
                parseFloat(pattern.diameter),
                substitutionLength);
        }
    });
    return Math.round(substitutionWeight, 0).toLocaleString();
}

const calculateYieldPercentage = (patterns) => {
    const preCutPatterns = patterns.filter(pattern => pattern.preCut);
    const notPreCutPatterns = patterns.filter(pattern => !pattern.preCut);
    const utilizedWeight = calculateTotalPatternsWeightExceptWaste(notPreCutPatterns);
    const preCutRebarsWeight = calculateTotalPatternsWeightExceptWaste(preCutPatterns);
    const totalWeight = calculateTotalPatternsWeight(patterns);
    const preCutWasteWeight = calculateWasteWeight(preCutPatterns);
    return (utilizedWeight + preCutRebarsWeight) / (totalWeight - preCutWasteWeight) * 100;
}

const calculateYieldExceptPreCutPercentage = (patterns) => {
    const notPreCutPatterns = patterns.filter(pattern => !pattern.preCut);
    const utilizedWeight = calculateTotalPatternsWeightExceptWaste(notPreCutPatterns);
    const totalWeight = calculateTotalPatternsWeight(notPreCutPatterns);
    return (utilizedWeight / totalWeight) * 100;
}

export const getYieldPercentage = (patterns) => {
    if (patterns.length === 0) {
        return 0;
    }
    return calculateYieldPercentage(patterns).toFixed(2);
}

export const getWastePercentage = (patterns) => {
    if (patterns.length === 0) {
        return 0;
    }
    return (100 - calculateYieldPercentage(patterns)).toFixed(2);
}

export const getYieldExceptPreCutPercentage = (patterns) => {
    if (patterns.length === 0) {
        return 0;
    }
    return calculateYieldExceptPreCutPercentage(patterns).toFixed(2);
}

export const getWasteExceptPreCutPercentage = (patterns) => {
    if (patterns.length === 0) {
        return 0;
    }
    return (100 - calculateYieldExceptPreCutPercentage(patterns)).toFixed(2);
}

export const getNumberOfTotalLogs = (patterns) => {
    let totalLogs = 0;
    patterns.forEach(pattern => {
        totalLogs += pattern.nLogs;
    });
    return Math.round(totalLogs, 0).toLocaleString();
}

export const getNumberOfOrderLogs = (patterns, currentFloors) => {
    const patternsInUpperFloors = patterns.filter(pattern => {
        const patternMinFloor = Math.min(...pattern.floors);
        const currentMinFloor = Math.min(...currentFloors);
        return pattern.floors.some(floor => currentFloors.includes(floor))
        && patternMinFloor >= currentMinFloor
    });
    const notPreCutPatterns = patternsInUpperFloors.filter(pattern => !pattern.preCut);

    let totalLogs = 0;
    notPreCutPatterns.forEach(pattern => {
        totalLogs += pattern.nLogs;
    });
    return Math.round(totalLogs, 0).toLocaleString();
}

export function getTheoreticalWeight(rebarInformation, allFloors, selectedDiameters, selectedFloors) {
    let totalWeight = 0;
    const rebarInformationList = Object.values(rebarInformation);
    const filteredRebarInformation = rebarInformationList.filter(rebar => {
        const diameter = rebar["donatiKodu"].split("-")[0];
        const floor = allFloors.map(x => x.label).indexOf(rebar["kat"]);
        return selectedDiameters.includes(diameter) && selectedFloors.includes(floor);
    });
    filteredRebarInformation.forEach(rebar => {
        const diameter = rebar["donatiKodu"].split("-")[0];
        const length = rebar["donatiKodu"].split("-")[1];
        if (parseFloat(diameter) === 28) {
            console.log(rebar)
        }
        const weight = rebar["adet"] * calculateRebarWeight(
            parseFloat(diameter),
            parseFloat(length)
        );
        totalWeight += weight;
    });
    return Math.round(totalWeight, 0).toLocaleString();
}